/**
 * 창 단축키 공용 가드다. 다음 표면이 함께 쓴다:
 * - 전체화면 창 셸(탭 이동)
 * - 영상 플레이어(재생)
 * - 에피소드 목록(이동)
 * - 리뷰 독(태그·저장)
 *
 * 이 핸들러들은 모두 `window` 에 등록된다. 포커스를 요구하지 않아야 사용자가 목록이나 영상을
 * 클릭하지 않고 바로 탐색할 수 있다. 하지만 그러면 화면에 보이지 않는 표면도 같은 키를 받는다.
 * 아래 가드들이 그 범위를 줄인다.
 */

/**
 * 이 표면이 지금 키를 받아도 되는지 판정한다.
 *
 * 전체화면 창(`FullscreenWindow`)이 열리면 뒤의 라우트 페이지는 `inert` 가 된다. 하지만 `inert` 는
 * window 레벨 keydown 을 막지 못한다. 가드가 없으면 보이지 않는 배경 화면에서 리뷰가 저장된다.
 * 창 위에 c6 Modal 이 열린 경우도 같은 규칙으로 막는다(스택이 창을 inert 로 잠근다).
 * 최소화된 창은 셸이 자식을 언마운트하므로 `isConnected` 로 함께 걸러진다.
 */
export function isHotkeySurfaceActive(root: HTMLElement | null): boolean {
  if (root === null || !root.isConnected) {
    return false;
  }
  return root.closest("[inert]") === null;
}

/** 입력 중인 요소(텍스트 입력·contenteditable)에 포커스가 있으면 단축키를 잡지 않는다. */
export function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tagName = target.tagName.toLowerCase();
  return (
    target.isContentEditable ||
    target.closest('[contenteditable="true"]') !== null ||
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select"
  );
}

/**
 * 범위 슬라이더(`input[type=range]`)인지 판정한다. 이 입력은 방향키·Home/End 만 쓰고 글자·숫자는
 * 받지 않는다. 그래서 문자 단축키만 쓰는 표면(리뷰 독)은 여기에 포커스가 있어도 키를 받아도 된다.
 * 공용 `isEditableKeyboardTarget` 에서 이 입력을 빼지 않는다. 방향키를 쓰는 표면(에피소드 ↑/↓)은
 * 이 입력에 포커스가 있을 때 키를 받지 않아야 하기 때문이다.
 */
export function isRangeInput(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement && target.type === "range";
}

/**
 * 네이티브 Enter 구역 표시다. 이 속성이 있는 구역 안의 버튼에 포커스가 있으면 Enter 는 그 버튼이
 * 처리한다. 창 단축키가 Enter 를 저장 같은 동작에 쓰면(리뷰 독) 버튼 활성화와 겹쳐 두 번 실행된다.
 */
const NATIVE_ENTER_REGION = "data-native-enter";

/** 구역 요소에 spread 로 추가하는 속성이다. */
export const nativeEnterRegionProps = { [NATIVE_ENTER_REGION]: "" } as const;

/** 네이티브 Enter 구역 안의 버튼류에 포커스가 있는지 판정한다. */
export function isNativeEnterTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  const control = target.closest('button, a[href], summary, [role="button"]');
  return control !== null && control.closest(`[${NATIVE_ENTER_REGION}]`) !== null;
}

/** 수식 키가 하나라도 눌려 있으면 앱 단축키가 아니라 브라우저·OS 조합으로 판정한다. */
export function hasModifierKey(event: KeyboardEvent): boolean {
  return event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
}
